/// <amd-module name="Wiki/Editor/Components/DropTarget" />
import * as React from "react";

import { css } from "VSS/Platform/Layout";

import * as Resources from "Wiki/Editor/Resources";
import { isSafari } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface IDropTargetProps {
    onDrop: (dataTransfer: DataTransfer) => void;
    onDragEnter?: (event?: React.DragEvent<HTMLDivElement>) => void;
    onDragLeave?: () => void;

    className?: string;
}

export interface IDropTargetState {
    draggingOver?: boolean;
    showDropFilesOverlay?: boolean;
}

// Some additional implementation notes.
// 1. When dragging over something, if you drag over a child element, that counts as 'leaving' the parent
// 2. But events still bubble up to the parent meaning the parent will get continous enter/leave events with different targets
// 3. Giving a child pointer-events: none can prevent this
// 4. Sometimes edge just likes to throw an 'unspecified error' but if you catch the error everything is cool?
// 5. Have to call preventDefault() and stopProgagation() on drag over events for firefox to work
// 6. We don't always get an enter event on the parent we expect. That is why I can't check event.target against this._dropTarget in enter. It may never fire
// 7. But because I then use css to disable pointer events on everything I can, checking against this._dropTarget is ok because we're left with only 1 element receiving events
// 8. Checking _dropTarget on leave seems key due to #6 and #1. Otherwise a lot of times we just receive a leave event immediately on a child and stop dragging even though we are totally still dragging
// 9. Have to set dropEffect on both enter and move for all browsers to understand it. But wrap it in try/catch in case edge is feeling moody
// 10. Sometimes the target for onDragEnter events is off on another part of the page nowhere near my mouse. Still haven't figure that one out.
// 11. onDrop is a cool event that actually makes sense. Usually.

/**
 * This component allows you to wrap any content and turn it into a drop target for files
 * It will place the child components inside a div that will block mouse interaction while
 * a file is being dragged over and will call onDrop with the files that have been dropped here
 */
export class DropTarget extends React.PureComponent<IDropTargetProps, IDropTargetState> {
    private _ref = React.createRef<HTMLDivElement>();

    constructor(props: IDropTargetProps) {
        super(props);
        this.state = {};
    }

    public render(): JSX.Element {
        return (
            <div
                className={css("droptarget-container", this.props.className)}
                onDragOver={this._onDragOver}
                onDragEnter={this._onDragEnter}
                onDragLeave={this._onDragLeave}
                onDrop={this._onDrop}
                ref={this._ref}
            >
                <div className={css("flex-column flex-grow", this.state.draggingOver ? "no-events" : "")}>
                    <DropOverlay showOverlay={!!this.state.showDropFilesOverlay}>{this.props.children}</DropOverlay>
                </div>
            </div>
        );
    }

    private _setDropEffect(event: React.DragEvent<HTMLDivElement>): boolean {
        const dataTransfer = event.dataTransfer;
        const noDropEffect = "none";
        if (dataTransfer) {
            let dropEffect: "link" | "copy" | "none";
            if (DragDropHelper.isDragDataTypeString(dataTransfer)) {
                dropEffect = "link";
            } else if (DragDropHelper.isDragDataTypeFile(dataTransfer)) {
                dropEffect = "copy";
            } else {
                dropEffect = noDropEffect;
            }

            try {
                /* Safari Browser dependent case, Safari only responds to "all" */
                if (isSafari() && dropEffect !== noDropEffect) {
                    dataTransfer.dropEffect = "all" as any;
                } else {
                    dataTransfer.dropEffect = dropEffect;
                }

                if (dropEffect != noDropEffect) {
                    return true;
                }
            } catch (err) {
                /* In Edge assigning dataTransfer.dropEffect throws exception, however we want
                 * the DropOverlay displayed if a file is being dragged.
                 */
                if (dataTransfer.types[0] === "Files") {
                    return true;
                }
            }
        }

        return false;
    }

    private _onDragEnter = (event: React.DragEvent<HTMLDivElement>): void => {
        if (this._setDropEffect(event)) {
            event.preventDefault();
            event.stopPropagation();
            this.setState({
                draggingOver: true,
                showDropFilesOverlay: DragDropHelper.isDragDataTypeFile(event.dataTransfer)
            });
            this.props.onDragEnter && this.props.onDragEnter(event);
        }
    };

    private _onDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        this._setDropEffect(event);
    };

    private _onDragLeave = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        const dataTransfer = event.dataTransfer;
        if (dataTransfer && event.target == this._ref.current) {
            this.setState({
                draggingOver: false,
                showDropFilesOverlay: false
            });
            this.props.onDragLeave && this.props.onDragLeave();
        }
    };

    private _onDrop = (event: React.DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        this.props.onDrop(event.dataTransfer);
        this.setState({
            draggingOver: false,
            showDropFilesOverlay: false
        });
        this.props.onDragLeave && this.props.onDragLeave();
    };
}

interface IDropOverlayProps {
    /**
     * Whether the overlay should be shown or not
     * When false, only this component's children will be shown
     */
    showOverlay: boolean;

    /**
     * You can use this to override the default overlay with either a different string or whatever content you want
     */
    overlayContent?: JSX.Element;

    /**
     * additional className to add to the content div
     */
    className?: string;
}

// export for UTs
/**
 * This component is used to show content on top of its children. We use this in drag and drop
 * to show something like
 *
 *   - - - - - - - - - - - -
 *  |    Drop files here    |
 *   - - - - - - - - - - - -
 *
 * inside a text area while dragging files to give a visual key of where to drop files when using
 * DropTarget. When used in combination with DropTarget, DropTarget must be an ancestor of DropOverlay
 * <DropTarget>
 *   <SomeHiearchy>
 *     <DropOverlay>
 *       <ContentIWantToShowOverlayOnTopOf/>
 *     </DropOverlay>
 *   </SomeHiearchy>
 * </DropTarget>
 *
 * showOverlay should be set to true/false based on whether DropTarget has called dragEnter or dragLeave
 *
 * You can either not specify any overlay content to get the default box pictured above,
 * or use defaultOverlayContent("my string") to use a different string than the default
 * or use your own content entirely.
 */
export class DropOverlay extends React.PureComponent<IDropOverlayProps, {}> {
    private _defaultOverlayContent = defaultOverlayContent();

    public render(): JSX.Element {
        return (
            <div className={css("dropoverlay flex-column flex-grow", this.props.showOverlay ? "showOverlay" : "")}>
                <div className={css("do-content", this.props.className, this.props.showOverlay ? "showOverlay" : "")}>
                    {this.props.overlayContent || this._defaultOverlayContent}
                </div>
                {this.props.children}
            </div>
        );
    }
}

const defaultOverlayContent = (content?: string): JSX.Element => {
    content = content || Resources.DefaultFileDropText;
    return <div className={"do-defaultcontent"}>{content}</div>;
};

export class DragDropHelper {
    public static getFileArrayFromDataTransfer(dataTransfer: DataTransfer): File[] {
        const fileArray: File[] = [];
        if (dataTransfer && dataTransfer.items) {
            for (let i = 0; i < dataTransfer.items.length; ++i) {
                const file = dataTransfer.items[i].getAsFile();
                if (file) {
                    fileArray.push(file);
                }
            }
        } else if (dataTransfer && dataTransfer.files) {
            // ie uses dataTransfer.files instead of dataTransfer.items
            for (let i = 0; i < dataTransfer.files.length; ++i) {
                const file = dataTransfer.files[i];
                if (file) {
                    fileArray.push(file);
                }
            }
        }

        return fileArray;
    }

    public static isDragDataTypeFile(dataTransfer: DataTransfer): boolean {
        if (
            (dataTransfer.items && dataTransfer.items[0] && dataTransfer.items[0].kind === "file") ||
            /* IE browser dependent check */
            dataTransfer.types[0] === "Files" ||
            /* Safari browser dependent check */
            dataTransfer.types[0] === "public.file-url"
        ) {
            return true;
        } else {
            return false;
        }
    }

    public static isDragDataTypeString(dataTransfer: DataTransfer): boolean {
        if (
            (dataTransfer.items && dataTransfer.items[0] && dataTransfer.items[0].kind === "string") ||
            /* IE browser dependent check */
            dataTransfer.types[0] === "Text" ||
            /* Safari browser dependent check */
            dataTransfer.types[0] === "public.utf8-plain-text"
        ) {
            return true;
        } else {
            return false;
        }
    }
}
